import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  isApiError,
  requiresExactCancellationRetry as requiresExactDropInCancellationRetry,
} from "@bw/shared/api";
import type { DropInCancellationDecisionResponse } from "@bw/shared/types/generated";
import { formatMoney } from "@bw/shared/utils";

const UNAVAILABLE_COPY: Record<string, string> = {
  pending_cancellation_disabled:
    "This facility does not allow pending check-ins to be cancelled.",
  already_started:
    "The cancellation window has ended because this session has already started.",
  no_show:
    "The staff validation window ended, so this check-in is now a no-show.",
  session_not_live: "This drop-in session is no longer active.",
  cancellation_recovery_pending:
    "A previous cancellation was interrupted. Continue it to finish reconciling this session.",
};

function deadlineLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const deadline = new Date(value);
  if (Number.isNaN(deadline.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(deadline);
}

export function DropInCancellationDialog({
  decision,
  decisionLoading,
  decisionError,
  actionsDisabled,
  cancelPending,
  cancelError,
  recoveryPending,
  recoveryError,
  decisionChanged,
  onDecisionRetry,
  onConfirm,
  onRecover,
  onClose,
}: {
  decision: DropInCancellationDecisionResponse | undefined;
  decisionLoading: boolean;
  decisionError: unknown;
  actionsDisabled: boolean;
  cancelPending: boolean;
  cancelError: unknown;
  recoveryPending: boolean;
  recoveryError: unknown;
  decisionChanged: boolean;
  onDecisionRetry: () => void;
  onConfirm: () => void;
  onRecover: () => void;
  onClose: () => void;
}) {
  const exactRetryRequired = requiresExactDropInCancellationRetry(cancelError);
  const closeLocked = cancelPending || recoveryPending || exactRetryRequired;
  const deadline = deadlineLabel(decision?.cancellation_deadline);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={() => {
        if (!closeLocked) onClose();
      }}
      aria-labelledby="dropin-cancellation-title"
      aria-describedby="dropin-cancellation-review"
      sx={{ "& .MuiButton-root.Mui-disabled": { pointerEvents: "auto" } }}
    >
      <DialogTitle id="dropin-cancellation-title">
        Cancel this drop-in session?
      </DialogTitle>
      <DialogContent>
        <Stack
          id="dropin-cancellation-review"
          spacing={2}
          aria-label="Drop-in cancellation decision"
        >
          {decisionLoading ? (
            <Typography color="text.secondary" role="status">
              Checking your cancellation terms...
            </Typography>
          ) : decisionError || !decision ? (
            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
              <Alert severity="error">
                We couldn&apos;t load the current cancellation decision.
              </Alert>
              <Button variant="outlined" size="small" onClick={onDecisionRetry}>
                Retry decision
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {decisionChanged ? (
                <Alert severity="warning">
                  The cancellation decision changed. Review the latest terms
                  before confirming.
                </Alert>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Policy version {decision.accepted_policy_version}
              </Typography>
              <Typography>{decision.accepted_policy_summary}</Typography>
              {decision.allowed ? (
                <Stack component="dl" spacing={1} sx={{ m: 0 }}>
                  <DecisionRow
                    label="Refund target"
                    value={`${formatMoney(
                      decision.refund_target_amount,
                      decision.currency,
                    )} ${decision.currency} total`}
                  />
                  <DecisionRow
                    label="Visit pass"
                    value={
                      decision.restore_package_visit
                        ? "1 visit will be restored"
                        : "No visit will be restored"
                    }
                  />
                  {Number(decision.refund_target_amount) > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Existing refunds count toward this total.
                    </Typography>
                  ) : null}
                  {deadline ? (
                    <Typography variant="body2" color="text.secondary">
                      Available until{" "}
                      <time dateTime={decision.cancellation_deadline ?? ""}>
                        {deadline}
                      </time>
                      .
                    </Typography>
                  ) : null}
                </Stack>
              ) : (
                <Alert severity="error">
                  {decision.unavailable_reason ===
                    "cancellation_recovery_pending" &&
                  !decision.recovery_pending
                    ? "Facility staff must finish this cancellation."
                    : (UNAVAILABLE_COPY[decision.unavailable_reason ?? ""] ??
                      "This session cannot be cancelled.")}
                </Alert>
              )}
            </Stack>
          )}

          {cancelError && !decisionChanged ? (
            <Alert severity="error">
              {isApiError(cancelError)
                ? cancelError.message
                : "We could not confirm whether the cancellation completed. Retry the exact same request."}
            </Alert>
          ) : null}
          {recoveryError ? (
            <Alert severity="error">
              {isApiError(recoveryError)
                ? recoveryError.message
                : "We could not finish reconciling the cancellation. Try continuing it again."}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={onClose} disabled={closeLocked}>
          Keep session
        </Button>
        {decision?.allowed && !decisionError ? (
          <Button
            color="error"
            variant="contained"
            onClick={onConfirm}
            disabled={actionsDisabled || cancelPending}
          >
            {cancelPending
              ? "Cancelling..."
              : exactRetryRequired
                ? "Retry exact cancellation"
                : "Yes, cancel session"}
          </Button>
        ) : null}
        {decision?.recovery_pending && !decisionError ? (
          <Button
            color="error"
            variant="contained"
            onClick={onRecover}
            disabled={actionsDisabled || recoveryPending}
          >
            {recoveryPending
              ? "Continuing cancellation..."
              : "Continue cancellation"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

function DecisionRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ justifyContent: "space-between", alignItems: "baseline" }}
    >
      <Typography component="dt" color="text.secondary">
        {label}
      </Typography>
      <Typography
        component="dd"
        sx={{ m: 0, textAlign: "right", fontWeight: 700 }}
      >
        {value}
      </Typography>
    </Stack>
  );
}
